import React, { useState } from 'react';
import { looseCategoryKey } from '../shared/vendorCategories';

/**
 * A category menu with a search box above it.
 * Typing narrows the list. The menu stays a native select so a phone
 * can scroll a short list instead of a long wheel.
 */
export default function SearchableCategorySelect({
  id,
  label = 'Category',
  value,
  onChange,
  options = [],
  extraOptions = [],
  emptyLabel = '',
  allowEmpty = true,
  required = false,
  colors = {},
  compact = false,
  keepUnknownValue = true,
  startOpen = false,
}) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(startOpen);
  const text = colors.text || '#111';
  const muted = colors.textSecondary || '#666';
  const surface = colors.surfaceAlt || colors.surface || '#fff';
  const border = colors.border || colors.borderLight || '#ddd';
  const field = {
    width: '100%',
    maxWidth: '100%',
    minHeight: '44px',
    fontSize: '16px',
    padding: '0.7rem 1rem',
    borderRadius: '1rem',
    border: `1px solid ${border}`,
    background: surface,
    color: text,
    boxSizing: 'border-box',
  };

  const rows = [];
  const seen = new Set();
  const push = (optionValue, optionLabel) => {
    const stored = String(optionValue == null ? '' : optionValue);
    const key = `${stored}\0${optionLabel}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ value: stored, label: optionLabel });
  };
  if (allowEmpty) push('', emptyLabel || 'All categories');
  extraOptions.forEach((option) => {
    if (!option) return;
    push(option.value, option.label || option.value);
  });
  (options || []).forEach((name) => push(name, name));
  if (keepUnknownValue && value && !rows.some((row) => row.value === value)) {
    push(value, value);
  }

  const needle = looseCategoryKey(query);
  const visible = rows.filter((row) => {
    if (!needle || row.value === value) return true;
    return looseCategoryKey(row.label).includes(needle);
  });
  const listSize = searching ? Math.min(8, Math.max(visible.length, 1)) : 1;

  return (
    <div style={{ marginBottom: compact ? 0 : '1rem', maxWidth: '100%', minWidth: compact ? '12rem' : 0, flex: compact ? '1 1 12rem' : undefined }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: '0.85rem', fontWeight: 650, marginBottom: '0.35rem', color: muted }}>
        {label}
      </label>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setSearching(true)}
        onBlur={() => setSearching(false)}
        placeholder="Search"
        aria-label={`Search ${label.toLowerCase()}`}
        autoComplete="off"
        enterKeyHint="search"
        style={{ ...field, marginBottom: '0.4rem' }}
      />
      <select
        id={id}
        value={visible.some((row) => row.value === (value || '')) ? (value || '') : (allowEmpty ? '' : (visible[0] && visible[0].value) || '')}
        required={required}
        {...(listSize > 1 ? { size: listSize } : {})}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setSearching(true)}
        onBlur={() => setSearching(false)}
        style={{ ...field, minHeight: listSize > 1 ? `${Math.min(listSize, 8) * 44}px` : '44px' }}
      >
        {visible.length === 0 ? <option value="">No matches</option> : null}
        {visible.map((row) => (
          <option key={`${row.value}:${row.label}`} value={row.value}>{row.label}</option>
        ))}
      </select>
    </div>
  );
}
